import { DropDownMenu } from '@app/ui/dropdown-menu/DropDownMenu'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import theme from '@app/ui/theme'
import React, { FunctionComponent, useRef, useState } from 'react'
import { useMediaQuery } from 'react-responsive'

import { ProjectCourse } from '../../../types/Project'
import HoleButton from '../../atoms/HoleButton'
import Spacer from '../../atoms/Spacer'
import * as S from './styled'
import { useRatingTemplate } from '../../../pages/CreateRatingTemplatePage/tabs/SelectHoles/hooks/useRatingTemplate'
import { RatingHole } from '../../../pages/CreateRatingTemplatePage/tabs/SelectHoles/hooks/useRatingTemplate/types'

type Props = {
  courses: ProjectCourse[]
  selectedCourse: ProjectCourse | undefined
  handleDropdownChange: (course: ProjectCourse) => void
  handleHolePress: (
    hole: RatingHole,
    courseName: string,
    courseId: string,
    holeIsSelected: boolean
  ) => void
  selectedHoles: RatingHole[]
  createCompositeCoursePress: () => void
  includeFirst9Press?: () => void
  includeLastNPress?: () => void
  footer?: React.ReactNode
}

const HoleSelector: FunctionComponent<Props> = ({
  courses,
  selectedCourse,
  handleDropdownChange,
  handleHolePress,
  createCompositeCoursePress: rateCoursePress,
  selectedHoles,
  includeFirst9Press,
  includeLastNPress,
  footer,
}) => {
  const { ratingHoles } = useRatingTemplate()
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1524px)' })

  const isHoleSelected = (physicalID: string) =>
    ratingHoles.some((rh) => rh.physicalID === physicalID)

  const first9Holes = selectedCourse?.holes.slice(0, 9) ?? []
  const lastNHoles = selectedCourse?.holes.slice(9) ?? []

  const isFirst9Selected =
    first9Holes.length > 0 && first9Holes.every((h) => isHoleSelected(h.physicalID))
  const isLastNSelected =
    lastNHoles.length > 0 && lastNHoles.every((h) => isHoleSelected(h.physicalID))
  const isAllSelected =
    (selectedCourse?.holes.length ?? 0) > 0 &&
    (selectedCourse?.holes ?? []).every((h) => isHoleSelected(h.physicalID))

  const [viewMode] = useState<'grid' | 'compact' | 'table'>('grid')

  const activeStyle = { backgroundColor: theme.colors.primary2 }
  const activeTextStyle = { color: theme.colors.white }
  const overlayRef = useRef() as React.MutableRefObject<HTMLDivElement>

  return (
    <S.OuterContainer>
      <div>
        <Spacer height={16} />
        <S.MiddleContainer>
          <div>
            <S.SelectorText>Course selector</S.SelectorText>
            <Spacer height={8} />
            {selectedCourse && +selectedCourse.holeCount > 9 ? (
              <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                <RoundedButton
                  title="Include first 9"
                  onPress={includeFirst9Press ?? rateCoursePress}
                  style={{ flexGrow: 1, ...(isFirst9Selected ? activeStyle : {}) }}
                  textStyle={isFirst9Selected ? activeTextStyle : undefined}
                />
                <RoundedButton
                  title={`Include last ${+selectedCourse.holeCount - 9}`}
                  onPress={includeLastNPress ?? rateCoursePress}
                  style={{ flexGrow: 1, ...(isLastNSelected ? activeStyle : {}) }}
                  textStyle={isLastNSelected ? activeTextStyle : undefined}
                />
                <RoundedButton
                  title={`Include all`}
                  onPress={rateCoursePress}
                  style={{ flexGrow: 1, ...(isAllSelected ? activeStyle : {}) }}
                  textStyle={isAllSelected ? activeTextStyle : undefined}
                />
              </div>
            ) : (
              <RoundedButton
                title={`Include all ${selectedCourse?.holeCount ?? 9}`}
                onPress={rateCoursePress}
              />
            )}
          </div>
        </S.MiddleContainer>
        <Spacer height={16} />
        <S.MiddleContainer>
          <DropDownMenu
            width={390}
            tablet={isTabletOrMobile}
            value={selectedCourse?.name}
            reference={overlayRef}
            relative
          >
            {courses.map((course) => (
              <div
                key={course.id}
                onClick={() => {
                  handleDropdownChange(course)
                  if (overlayRef?.current) {
                    overlayRef.current.click()
                  }
                }}
                style={{
                  padding: '8px 16px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #CECECE',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span>{course.name}</span>
                <span>{course.holeCount} holes</span>
              </div>
            ))}
          </DropDownMenu>
        </S.MiddleContainer>
        <Spacer height={16} />
        {selectedCourse && (
          <>
            {viewMode === 'grid' && (
              <S.ContentContainer>
                {selectedCourse.holes.map((hole) => {
                  const holeIsSelected = ratingHoles.some(
                    (ratingHole) => ratingHole.physicalID === hole.physicalID
                  )
                  const holeCount = ratingHoles.filter(
                    (ratingHole) => ratingHole.physicalID === hole.physicalID
                  ).length
                  const onPress = () => {
                    handleHolePress(
                      { ...hole, courseName: selectedCourse.name, courseHoleNumber: +hole.courseHoleNumber, ratingHoleNumber: 0, par: +hole.par },
                      selectedCourse.name,
                      selectedCourse.id,
                      !!holeIsSelected
                    )
                  }
                  return (
                    <S.HoleContainer key={hole.physicalID} style={{ marginBottom: 24 }}>
                      {holeCount >= 2 && <S.DuplicateBadge>{holeCount}</S.DuplicateBadge>}
                      <HoleButton
                        holeNr={+hole.courseHoleNumber}
                        ghNr={+hole.globalHoleNumber}
                        par={+hole.par}
                        selected={!!holeIsSelected}
                        onPress={onPress}
                      />
                    </S.HoleContainer>
                  )
                })}
              </S.ContentContainer>
            )}

            {viewMode === 'compact' && (
              <S.CompactContentContainer>
                {selectedCourse.holes.map((hole) => {
                  const holeIsSelected = ratingHoles.some(
                    (ratingHole) => ratingHole.physicalID === hole.physicalID
                  )
                  return (
                    <S.CompactHoleItem
                      key={hole.physicalID}
                      selected={!!holeIsSelected}
                      onClick={() => {
                        handleHolePress(
                          { ...hole, courseName: selectedCourse.name, courseHoleNumber: +hole.courseHoleNumber, ratingHoleNumber: 0, par: +hole.par },
                          selectedCourse.name,
                          selectedCourse.id,
                          !!holeIsSelected
                        )
                      }}
                    >
                      <S.CompactHoleNumber selected={!!holeIsSelected}>{+hole.courseHoleNumber}</S.CompactHoleNumber>
                      <S.CompactHolePar selected={!!holeIsSelected}>P{+hole.par}</S.CompactHolePar>
                    </S.CompactHoleItem>
                  )
                })}
              </S.CompactContentContainer>
            )}

            {viewMode === 'table' && (
              <S.TableContainer>
                <S.StyledTable>
                  <S.TableHead>
                    <tr>
                      <S.TableHeaderCell>Hole</S.TableHeaderCell>
                      <S.TableHeaderCell>Par</S.TableHeaderCell>
                      <S.TableHeaderCell>GH#</S.TableHeaderCell>
                      <S.TableHeaderCell></S.TableHeaderCell>
                    </tr>
                  </S.TableHead>
                  <tbody>
                    {selectedCourse.holes.map((hole) => {
                      const holeIsSelected = ratingHoles.some(
                        (ratingHole) => ratingHole.physicalID === hole.physicalID
                      )
                      return (
                        <S.TableRow
                          key={hole.physicalID}
                          selected={!!holeIsSelected}
                          onClick={() => {
                            handleHolePress(
                              { ...hole, courseName: selectedCourse.name, courseHoleNumber: +hole.courseHoleNumber, ratingHoleNumber: 0, par: +hole.par },
                              selectedCourse.name,
                              selectedCourse.id,
                              !!holeIsSelected
                            )
                          }}
                        >
                          <S.TableCell>Hole {+hole.courseHoleNumber}</S.TableCell>
                          <S.TableCell>{+hole.par}</S.TableCell>
                          <S.TableCell>{+hole.globalHoleNumber}</S.TableCell>
                          <S.TableCell>
                            <S.SelectedPill selected={!!holeIsSelected}>
                              {holeIsSelected ? '✓' : ''}
                            </S.SelectedPill>
                          </S.TableCell>
                        </S.TableRow>
                      )
                    })}
                  </tbody>
                </S.StyledTable>
              </S.TableContainer>
            )}
          </>
        )}
      </div>
      {!isTabletOrMobile && <Spacer height={32} />}
      {footer}
    </S.OuterContainer>
  )
}

export default HoleSelector
